<!--
	@name app-conference-section
	@description Conference section
	@date 2020/05/20
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :id="id" :component="$options.name" class="scroll-margin--fixed-header-offset" :href="`#${id}`">
		<v-divider v-if="divider" inset />
		<v-container class="py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }">
			<v-row>
				<v-col :order="reverse && $vuetify.breakpoint.mdAndUp ? 'first' : 'last'" cols="12" md="6">
					<v-sheet
						v-if="membersOnly && !member"
						width="100%"
						height="320"
						class="rounded d-flex align-center justify-center"
						style="position: relative; overflow: hidden; transform: translate3d(0,0,0);"
					>
						<div class="bg" />
						<div class="bg bg2" />
						<div class="bg bg3" />
						<div class="text-h5 font-weight-bold pa-10 text-center">
							<span class="link--special">
								Member Exclusive
							</span>
							<p class="text-body-1 mt-4 px-1 mb-0">
								Enjoy access to premium content designed to support your growth, sharpen your strategy, and help your business thrive.
							</p>
							<div class="ma-n2 pt-5">
								<v-btn color="primary" class="ma-2" @click="login">
									Log in
								</v-btn>
								<v-btn color="primary" class="ma-2" to="/register">
									Register
								</v-btn>
							</div>
						</div>
					</v-sheet>
					<div v-else-if="vimeo" class="d-block rounded" style="padding:56.25% 0 0 0;position:relative; overflow: hidden;">
						<iframe :src="`https://player.vimeo.com/video/${vimeo}?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`" frameborder="0" allow="autoplay;fullscreen; picture-in-picture; clipboard-write; encrypted-media" style="position:absolute;top:0;left:0;width:100%;height:100%;" />
					</div>
					<video
						v-else-if="video"
						:src="video"
						width="100%"
						controls
						class="rounded"
					/>
					<v-sheet
						v-else
						width="100%"
						height="320"
						class="rounded d-flex align-center justify-center"
						style="position: relative; overflow: hidden; transform: translate3d(0,0,0);"
					>
						<div class="bg" />
						<div class="bg bg2" />
						<div class="bg bg3" />
						<div class="text-h5 font-weight-bold pa-10">
							Video coming soon
						</div>
					</v-sheet>
				</v-col>
				<v-col :order="reverse && $vuetify.breakpoint.mdAndUp ? 'last' : 'first'" cols="12" md="6" :class="reverse ? 'pl-md-10' : 'pr-md-10'">
					<p v-if="title" class="text-body-1 font-weight-medium font-italic mb-1" :class="{ 'white--text':dark }">
						{{ title }}
					</p>
					<p class="text-h5 font-weight-bold mb-4" :class="{ 'white--text':dark }">
						{{ subtitle }}
					</p>
					<slot name="copy" />
					<div v-if="avatarDisplay === 'dense'" class="ma-n3 d-lg-flex flex-wrap" :class="{ 'pt-7':avatarSize === 'large', 'pt-6':avatarSize !== 'large' }">
						<conference-avatar
							v-for="avatar in avatars"
							:key="avatar.name"
							:name="avatar.name"
							:position="avatar.position"
							:image="avatar.image"
							:size="avatarSize"
							:basis="avatarBasis"
							:dark="dark"
						/>
					</div>
				</v-col>
			</v-row>
			<div v-if="avatarDisplay === 'fullWidth'" class="pt-6 ma-n3 d-flex flex-wrap">
				<conference-avatar
					v-for="avatar in avatars"
					:key="avatar.name"
					:name="avatar.name"
					:position="avatar.position"
					:image="avatar.image"
					:basis="100 / avatars.length"
				/>
			</div>
		</v-container>
	</div>
</template>

<script>
	import ConferenceAvatar from '@/component/app/air-later-life/conference/avatar';

	export default {
		name: 'app-conference-section',

		components: {
			ConferenceAvatar
		},

        props: {
			id: { type: String, required: true },
			title: { type: String, required: false, default: null },
			subtitle: { type: String, required: false, default: null },
			avatars: { type: Array, required: false, default: null },
			avatarBasis: { type: Number, default: 50 },
			avatarSize: { type: String, default: 'medium' },
			avatarDisplay: { type: String, default: 'dense' },
			vimeo: {type: String, default: null },
			video: {type: String, default: null },
			reverse: { type: Boolean, default: false },
			divider: { type: Boolean, default: true },
			dark: { type: Boolean, default: false },
			membersOnly: { type: Boolean, default: false },
			member: { type: Boolean, default: false }
		},

		data() {
			return {};
		},

		computed: {},

		created() {},

		methods: {
			login() {
				this.$emit('login');
			},

			handleCtaClick(cta) {
                this.$emit('handle-cta-click', cta);
            }
		}
	};
</script>

<style lang="scss" scoped>
	.bg {
		animation:slide 3s ease-in-out infinite alternate;
		background-image: linear-gradient(-60deg, tint($col1, 75%) 50%, tint($col5, 75%) 50%);
		bottom:0;
		left:-50%;
		opacity:.5;
		position:fixed;
		right:-50%;
		top:0;
		z-index:-1;

		&--alt {
			background-image: linear-gradient(-60deg, tint($col3, 65%) 50%, tint($col4, 75%) 50%);
		}
	}

	.bg2 {
		animation-direction:alternate-reverse;
		animation-duration:4s;
	}

	.bg3 {
		animation-duration:5s;
	}

	@keyframes slide {
		0% {
			transform:translateX(-25%);
		}
		100% {
			transform:translateX(25%);
		}
	}

	.link--special {
		background-image: url($VUE_APP_S3_AIR_ASSETS_URL + '/manager_documents/67c606a5-0c64-46c2-a179-d47dce99378d.svg');
        background-position: 50% 50%;
        padding: 8px 16px;
		margin: 0px !important;
        background-repeat: no-repeat;
        background-size: cover;
    }

	.scroll-margin--fixed-header-offset {
        scroll-margin: 140px;
    }
</style>
